/**
 * PROJECTION REPORT COMPONENT
 * Downloads the projections report for a date range as-is (excel file)
 */

import { Component, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { Popup } from '../../../core/shared/components/popup/popup';
import { ReportService } from '../shared/services/report.service';
import { ReportDateRange } from '../shared/models/report.model';

@Component({
  selector: 'app-projection-report',
  standalone: true,
  imports: [Popup, TranslatePipe],
  template: `
    <div class="flex flex-col gap-6 p-6">
      <!-- Header Section -->
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-lg sm:text-xl font-semibold text-[#444A6D]">{{ 'REPORTS.PROJECTION.TITLE' | translate }}</h1>
          <p class="text-gray-600 mt-1 text-sm">{{ 'REPORTS.PROJECTION.SUBTITLE' | translate }}</p>
        </div>
        <button
          (click)="openFilterModal()"
          class="text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2"
        >
          🔍 {{ 'REPORTS.GENERATE_REPORT' | translate }}
        </button>
      </div>

      <!-- Filter Modal -->
      <app-popup
        [show]="showFilterModal"
        title="REPORTS.SELECT_DATE_RANGE"
        (showChange)="showFilterModal = $event"
        [fields]="filterFields"
        [data]="filterData"
        (formChange)="onFilterChange($event)"
        [onSave]="onGenerateReport"
        saveButtonText="COMMON.GET_REPORT"
      ></app-popup>

      <!-- Loading State -->
      @if (isLoading) {
        <div class="flex justify-center items-center h-64">
          <div class="text-center">
            <div
              class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
            ></div>
            <p class="mt-4 text-gray-600">{{ 'REPORTS.LOADING_REPORT' | translate }}</p>
          </div>
        </div>
      }

      <!-- Error State -->
      @if (errorMessage) {
        <div class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          ❌ {{ errorMessage | translate }}
          <button (click)="clearError()" class="ms-4 underline font-semibold">{{ 'REPORTS.DISMISS' | translate }}</button>
        </div>
      }

      <!-- Empty State -->
      @if (!isLoading && !errorMessage) {
        <div class="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
          <span class="text-6xl">📊</span>
          <p class="mt-4 text-xl text-gray-600">{{ 'REPORTS.NO_REPORT_DOWNLOADED' | translate }}</p>
          <p class="text-gray-500">{{ 'REPORTS.EMPTY_HINT_DOWNLOAD' | translate }}</p>
        </div>
      }
    </div>
  `,
})
export class ProjectionReportComponent {
  private reportService = inject(ReportService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);

  showFilterModal = false;
  isLoading = false;
  errorMessage = '';

  // Filter form configuration
  filterFields: DynamicFieldConfig[] = [
    {
      key: 'startDate',
      label: 'REPORTS.START_DATE',
      type: 'date',
      validations: { required: true, noPastDate: true },
      gridClass: 'col-span-1',
    },
    {
      key: 'endDate',
      label: 'REPORTS.END_DATE',
      type: 'date',
      validations: { required: true, noPastDate: true },
      gridClass: 'col-span-1',
    },
  ];

  filterData: { startDate: string; endDate: string } = {
    startDate: '',
    endDate: '',
  };

  /**
   * Open filter modal with an empty form every time
   */
  openFilterModal(): void {
    this.filterData = { startDate: '', endDate: '' };
    this.showFilterModal = true;
  }

  /**
   * Handle filter form changes
   */
  onFilterChange(data: any): void {
    this.filterData = data;
  }

  /**
   * Generate report - returns a function for the popup component
   */
  onGenerateReport = (data: any): void => {
    this.generateReport(data);
  };

  /**
   * Main report download logic
   */
  private generateReport(filterData: any): void {
    if (!filterData.startDate || !filterData.endDate) {
      this.errorMessage = 'REPORTS.SELECT_BOTH_DATES_ERROR';
      this.toastr.error(this.translate.instant('REPORTS.SELECT_BOTH_DATES'));
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const dateRange: ReportDateRange = {
      startDate: filterData.startDate,
      endDate: filterData.endDate,
    };

    this.reportService.getProjectionReport(dateRange).subscribe({
      next: (blob) => {
        this.isLoading = false;
        // API returns the real .xlsx workbook binary — save it as-is.
        saveAs(blob, `ProjectionReport_${filterData.startDate}_to_${filterData.endDate}.xlsx`);
        this.toastr.success(this.translate.instant('REPORTS.DOWNLOAD_SUCCESS'));
      },
      error: (error) => {
        this.isLoading = false;
        const errMsg =
          error?.error?.message || error?.message || 'REPORTS.GENERATE_FAILED';
        this.errorMessage = errMsg;
        this.toastr.error(this.translate.instant(errMsg));
      },
    });
  }

  /**
   * Clear error message
   */
  clearError(): void {
    this.errorMessage = '';
  }
}
